/**
 * Dashboard Algorithms Route
 * List and manage trading algorithms
 */

import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { useAppStore } from '@/lib/store'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/trading/card'
import { Badge, StatusBadge } from '@/components/trading/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/trading/input'
import { Plus, Edit, Trash2, Play, Square, TrendingUp, Activity, Target } from 'lucide-react'
import { formatNumber, formatPercent } from '@/lib/utils'

export const Route = createFileRoute('/dashboard/algorithms')({
  component: AlgorithmsList,
})

function AlgorithmsList() {
  const navigate = useNavigate()
  const { state, actions } = useAppStore()
  const [searchQuery, setSearchQuery] = useState('')
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'inactive' | 'testing'>('all')

  // Mock data if no algorithms
  const mockAlgorithms = [
    {
      id: '1',
      name: 'Trend Following EUR/USD',
      description: 'Captures trends in EUR/USD using moving average crossovers',
      strategy: 'trend-following',
      symbol: 'EUR/USD',
      timeframe: '1h',
      status: 'active' as const,
      performance: {
        totalReturn: 1250,
        totalReturnPercent: 12.5,
        winRate: 65,
        profitFactor: 2.1,
        sharpeRatio: 1.8,
        maxDrawdown: 8.5,
        totalTrades: 45,
      },
      createdAt: '2024-01-15',
    },
    {
      id: '2',
      name: 'Mean Reversion GBP/USD',
      description: 'Exploits mean reversion patterns in GBP/USD',
      strategy: 'mean-reversion',
      symbol: 'GBP/USD',
      timeframe: '15m',
      status: 'inactive' as const,
      performance: {
        totalReturn: 450,
        totalReturnPercent: 4.5,
        winRate: 58,
        profitFactor: 1.6,
        sharpeRatio: 1.2,
        maxDrawdown: 12.3,
        totalTrades: 28,
      },
      createdAt: '2024-02-01',
    },
    {
      id: '3',
      name: 'Momentum BTC/USD',
      description: 'Momentum-based strategy for Bitcoin trading',
      strategy: 'momentum',
      symbol: 'BTC/USD',
      timeframe: '4h',
      status: 'testing' as const,
      performance: {
        totalReturn: 0,
        totalReturnPercent: 0,
        winRate: 0,
        profitFactor: 0,
        sharpeRatio: 0,
        maxDrawdown: 0,
        totalTrades: 0,
      },
      createdAt: '2024-03-10',
    },
  ]

  const algorithms = state.algorithms.length > 0 ? state.algorithms : mockAlgorithms

  // Filter algorithms
  const filteredAlgorithms = algorithms.filter((algo) => {
    const matchesSearch = algo.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      algo.symbol.toLowerCase().includes(searchQuery.toLowerCase())
    const matchesStatus = filterStatus === 'all' || algo.status === filterStatus
    return matchesSearch && matchesStatus
  })

  const handleDelete = (id: string) => {
    if (confirm('Are you sure you want to delete this algorithm?')) {
      actions.deleteAlgorithm(id)
    }
  }

  const handleToggleStatus = (algorithm: any) => {
    const newStatus = algorithm.status === 'active' ? 'inactive' : 'active'
    actions.updateAlgorithm(algorithm.id, { status: newStatus })
  }

  return (
    <div className="p-8 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">My Algorithms</h1>
          <p className="text-muted-foreground mt-1">
            Manage your trading algorithms
          </p>
        </div>
        <Link to="/dashboard/algorithms/new">
          <Button>
            <Plus className="h-4 w-4 mr-2" />
            Create Algorithm
          </Button>
        </Link>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="py-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <Input
                placeholder="Search algorithms..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <div className="flex gap-2">
              <Button
                variant={filterStatus === 'all' ? 'default' : 'outline'}
                onClick={() => setFilterStatus('all')}
                size="sm"
              >
                All ({algorithms.length})
              </Button>
              <Button
                variant={filterStatus === 'active' ? 'default' : 'outline'}
                onClick={() => setFilterStatus('active')}
                size="sm"
              >
                Active ({algorithms.filter((a) => a.status === 'active').length})
              </Button>
              <Button
                variant={filterStatus === 'inactive' ? 'default' : 'outline'}
                onClick={() => setFilterStatus('inactive')}
                size="sm"
              >
                Inactive ({algorithms.filter((a) => a.status === 'inactive').length})
              </Button>
              <Button
                variant={filterStatus === 'testing' ? 'default' : 'outline'}
                onClick={() => setFilterStatus('testing')}
                size="sm"
              >
                Testing ({algorithms.filter((a) => a.status === 'testing').length})
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Algorithms Grid */}
      {filteredAlgorithms.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            <Activity className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p className="text-lg mb-2">No algorithms found</p>
            <p className="text-sm mb-4">
              {searchQuery || filterStatus !== 'all'
                ? 'Try adjusting your filters'
                : 'Create your first algorithm to get started'}
            </p>
            {!searchQuery && filterStatus === 'all' && (
              <Link to="/dashboard/algorithms/new">
                <Button>
                  <Plus className="h-4 w-4 mr-2" />
                  Create Algorithm
                </Button>
              </Link>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {filteredAlgorithms.map((algorithm) => (
            <Card key={algorithm.id} className="hover:border-primary/50 transition-colors">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <Link to={`/dashboard/algorithms/${algorithm.id}`}>
                      <CardTitle className="text-lg hover:text-primary transition-colors cursor-pointer">
                        {algorithm.name}
                      </CardTitle>
                    </Link>
                    <p className="text-sm text-muted-foreground mt-1">{algorithm.description}</p>
                    <div className="flex items-center gap-2 mt-2">
                      <Badge variant="outline" size="sm">{algorithm.symbol}</Badge>
                      <Badge variant="outline" size="sm">{algorithm.timeframe}</Badge>
                      <StatusBadge status={algorithm.status} />
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {/* Performance Metrics */}
                {algorithm.status !== 'testing' && algorithm.performance.totalTrades > 0 ? (
                  <div className="grid grid-cols-2 gap-4 text-sm mb-4">
                    <div>
                      <span className="text-muted-foreground">Return: </span>
                      <span className={`font-semibold ${algorithm.performance.totalReturn >= 0 ? 'text-success' : 'text-destructive'}`}>
                        {algorithm.performance.totalReturn >= 0 ? '+' : ''}{formatPercent(algorithm.performance.totalReturnPercent)}
                      </span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Win Rate: </span>
                      <span className="font-semibold">{formatPercent(algorithm.performance.winRate)}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Sharpe: </span>
                      <span className="font-semibold">{algorithm.performance.sharpeRatio.toFixed(2)}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Max DD: </span>
                      <span className="font-semibold text-destructive">{formatPercent(algorithm.performance.maxDrawdown)}</span>
                    </div>
                  </div>
                ) : (
                  <div className="text-sm text-muted-foreground mb-4">
                    No performance data yet. Run a backtest to see results.
                  </div>
                )}

                {/* Actions */}
                <div className="flex items-center justify-between pt-4 border-t border-border">
                  <div className="flex gap-2">
                    <Link to={`/dashboard/algorithms/${algorithm.id}`}>
                      <Button variant="outline" size="sm">
                        <Edit className="h-3 w-3 mr-1" />
                        Edit
                      </Button>
                    </Link>
                    <Link to={`/dashboard/backtests/new?algorithmId=${algorithm.id}`}>
                      <Button variant="outline" size="sm">
                        <TrendingUp className="h-3 w-3 mr-1" />
                        Backtest
                      </Button>
                    </Link>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleToggleStatus(algorithm)}
                    >
                      {algorithm.status === 'active' ? (
                        <>
                          <Square className="h-3 w-3 mr-1" />
                          Stop
                        </>
                      ) : (
                        <>
                          <Play className="h-3 w-3 mr-1" />
                          Start
                        </>
                      )}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDelete(algorithm.id)}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  )
}
